import React from "react";

import { css } from "@emotion/css";
import { zodResolver } from "@hookform/resolvers/zod";
import { Resolver, useForm, useWatch, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { diabetesTypeTranslationMapping, medicationSchemaTranslationMapping } from "@/models/ConditionModel.ts";
import { pumpDeviceBrands, pumpModelTranslationMapping } from "@/models/MedicalDeviceModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { ColoredCard } from "@/uiKit/atoms/ColoredCard.tsx";
import { Form } from "@/uiKit/atoms/forms/Form.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { BirthDateFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/BirthDateFormField.tsx";
import { DiabetesTypeFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/DiabetesTypeFormField.tsx";
import { DoctorSelectFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/DoctorSelectFormField.tsx";
import { InclusionReasonFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/InclusionReasonFormField.tsx";
import { InsulinSchemeFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/InsulinSchemeFormField.tsx";
import { PatientEmailFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/PatientEmailFormField.tsx";
import { PatientFirstNameFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/PatientFirstNameFormField.tsx";
import { PatientHelperPhoneFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/PatientHelperPhoneFormField.tsx";
import { PatientLastNameFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/PatientLastNameFormField.tsx";
import { PatientPhoneFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/PatientPhoneFormField.tsx";
import { PumpBrandFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/PumpBrandFormField.tsx";
import { TermDateFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/TermDateFormField.tsx";
import {
  NursePreInclusionInfo,
  nursePreInclusionInfoSchema,
} from "@/uiKit/organisms/PatientCreationAndRenewal/NurseInclusion/NursePreInclusionInfo.ts";
import { useResetInsulinSchemeWhenInvalidDiabetesType } from "@/uiKit/organisms/PatientCreationAndRenewal/utils.ts";

export const NURSE_INCLUSION_FORM_ID = "NURSE_INCLUSION_FORM";

export type NursePreInclusionFormProps = {
  onSubmit: (data: NursePreInclusionInfo) => void;
};

export const NursePreInclusionForm: React.FC<NursePreInclusionFormProps> = ({ onSubmit }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const directives = Queries.practitioner.useNursePreInclusionDirectives({});

  const form = useForm<NursePreInclusionInfo>({
    resolver: zodResolver(nursePreInclusionInfoSchema) as Resolver<NursePreInclusionInfo>,
    defaultValues: {
      doctorId: directives.doctors.length === 1 ? directives.doctors[0].id : undefined,
    },
  });

  const diabetesType = useWatch({
    name: "diabetesType",
    control: form.control,
  });
  const insulinScheme = useWatch({
    name: "insulinScheme",
    control: form.control,
  });
  useResetInsulinSchemeWhenInvalidDiabetesType(form as unknown as UseFormReturn, diabetesType, insulinScheme);

  return (
    <Form id={NURSE_INCLUSION_FORM_ID} formContext={form} onSubmit={onSubmit} className={styles.root}>
      <ColoredCard color="blue" className={styles.title}>
        <CardContent>
          <Typography>{t("nurseInclusion.banner")}</Typography>
        </CardContent>
      </ColoredCard>
      <DoctorSelectFormField doctors={directives.doctors} />
      <Typography variant="subtitle" className={styles.title}>
        {t("nurseInclusion.patientInfo")}
      </Typography>
      <div className={styles.sideBySide}>
        <PatientFirstNameFormField />
        <PatientLastNameFormField />
      </div>
      <div className={styles.sideBySide}>
        <PatientEmailFormField />
        <PatientPhoneFormField />
      </div>
      <BirthDateFormField labelType="left" />
      <PatientHelperPhoneFormField labelType="left" />
      <Typography variant="subtitle" className={styles.title}>
        {t("diabetesForms.diabetesAndSchema")}
      </Typography>
      <DiabetesTypeFormField
        allowedDiabetesTypes={["type1", "type2", "gestational"]}
        translationMapping={type => diabetesTypeTranslationMapping(type, undefined)}
      />
      <InsulinSchemeFormField
        allowedInsulinSchemes={["insulinPen", "insulinPump", "noInsulin"]}
        diabetesType={diabetesType}
        translationMapping={medicationSchemaTranslationMapping}
      />
      {insulinScheme === "insulinPump" && (
        <PumpBrandFormField brands={pumpDeviceBrands} translationMapping={pumpModelTranslationMapping} />
      )}
      {diabetesType === "gestational" && <TermDateFormField />}
      <Typography variant="subtitle" className={styles.title}>
        {t("nurseInclusion.inclusionReason")}
      </Typography>
      <InclusionReasonFormField />
    </Form>
  );
};

const makeStyles = (theme: Theme) => ({
  root: css({
    display: "flex",
    flexDirection: "column",
  }),
  category: css({
    display: "flex",
    flexDirection: "column",
  }),
  title: css({
    marginBottom: theme.spacing(12),
  }),
  formRow: css({
    display: "flex",
    flexDirection: "row",
    width: "100%",
  }),
  formItem: css({
    paddingTop: theme.spacing(8),
  }),
  sideBySide: css({
    justifyContent: "space-between",
    alignItems: "flex-start",
    display: "flex",
    gap: theme.spacing(16),
    width: "100%",
  }),
  birthDateAside: css({
    alignItems: "flex-end",
  }),
});
